import { z } from 'zod';
import { UNIT_PATTERN } from './naming.ts';

/**
 * `data/masterplan.json` — where each unit sits on the site.
 *
 * The real site plan, the unit count and the numbering are unanswered (§15),
 * so this ships `provisional: true` with only the two documented product
 * instances placed. The interface says so on screen; it does not pretend.
 *
 * The schema scales without a code change: the operator adds plots, sets
 * `provisional` to false, and the masterplan is real. Units that share a
 * `modelSet` are instanced from one model, so adding twenty villas adds twenty
 * transforms and zero megabytes (§10).
 */

export const PlotSchema = z.strictObject({
  unitId: z.string().regex(UNIT_PATTERN),
  /** Metres, site-local. +x east, +z south, ground plane at y = 0. */
  x: z.number(),
  z: z.number(),
  /** Degrees clockwise about +y. Turns the model's front to the street. */
  rotationDeg: z.number().default(0),
  positionSource: z.enum(['surveyed', 'provisional']).default('provisional'),
});

export const StreetSchema = z.strictObject({
  /** Centre-line, metres site-local. Streets run east–west along x. */
  z: z.number(),
  width: z.number().positive(),
});

export const MasterplanSchema = z.strictObject({
  /** True until the operator supplies the approved site plan. */
  provisional: z.boolean(),
  provisionalNoteAr: z.string().min(1),
  provisionalNoteEn: z.string().min(1),
  site: z.strictObject({
    width: z.number().positive(),
    depth: z.number().positive(),
  }),
  /** Degrees the site's +z axis is rotated from true north. */
  northAzimuthDeg: z.number().default(0),
  /**
   * Internal streets. Optional: when absent the generators fall back to one
   * street per distinct `streetWidth` at the site edges, which is what the
   * two-unit provisional plan shipped with.
   */
  streets: z.array(StreetSchema).optional(),
  plots: z.array(PlotSchema).min(1),
});

export type Plot = z.infer<typeof PlotSchema>;
export type Street = z.infer<typeof StreetSchema>;
export type Masterplan = z.infer<typeof MasterplanSchema>;

export function plotFor(
  masterplan: Masterplan,
  unitId: string,
): Plot | undefined {
  return masterplan.plots.find((p) => p.unitId === unitId);
}
